<template>
  <div class="OrderConfirmScreenContainer" v-if="order">
    <div class="Container">
      <!-- Left Side View which is Product Information -->
      <div class="Preview">
        <div class="Preview-Body">
          <div class="PreviewImageArea">
            <img
              v-if="order.detail.product"
              class="PreviewImageArea-Image"
              :src="order.detail.product.imageUrl[0]"
              alt
            />
            <p v-else class="center">商品選択なし</p>
          </div>
          <div class="PreviewDescriptionArea" v-if="order.detail.product">
            <h2>{{ order.detail.productVariation.id }}</h2>
            <label class="product-variation">
              {{ order.detail.product.name }} /
              {{ getSizeText() }}
            </label>
          </div>
        </div>
      </div>

      <!-- Center View which is Preview view -->
      <div class="PreviewCenter">
        <h2 class="ColumnTitle">プレビュー</h2>
        <TabletPreview
          :order="order"
          :backColor="backColor"
          :frontColor="frontColor"
          :is-show-description="true"
          :is-adjust-description="true"
        />
      </div>

      <!-- Detail Information -->
      <div class="DetailInformationContainer">
        <div
          v-if="
            order.detail.product &&
            order.detail.product.name !== '保志以外へのご注文' &&
            order.detail.product.name !== '商品保留'
          "
        >
          <div
            class="SaveOrderButtonContainer"
            v-if="computedShowRequestOrderButton"
          >
            <!-- CLIENT_CONTRIBUTOR の場合 -->
            <el-button
              v-if="order.detail.product && isClientContriburor"
              type="primary"
              :loading="requestDialogVisible ? isLoading : false"
              @click="getRequestOrderDialog()"
            >
              確認依頼を出す
            </el-button>

            <!-- CLIENT_USER の場合 -->
            <template v-else>
              <SignatureInput
                :signature="detail.signature"
                @onSubmit="onSubmitSignature"
              />
              <el-button
                type="primary"
                style="margin-left: 10px;"
                :loading="sendOrderDialogVisible ? isLoading : false"
                @click="getSendOrderDialog()"
              >
                注文を送信する
              </el-button>
            </template>
          </div>
        </div>
        <div class="smoothScroll">
          <InformationTable
            title="店舗情報"
            :source="order.clientGroupInformation"
          />
          <InformationTable
            title="お客様情報"
            :source="order.customerInformation"
          />
          <InformationTable
            title="自由項目"
            :source="order.customFieldInformation"
          />
          <InformationTable
            title="商品情報"
            :source="order.categoryInformation"
          />
          <InformationTableChild :source="order.productInformation" />
          <InformationTable
            title="原稿情報"
            :source="order.orderTypeInformation"
          />
          <InformationTableChild :source="order.typographyInformation" />
          <InformationTable
            title="詳細情報"
            :source="order.detailInformation"
          />
          <div
            v-if="
              order.detail.customFamilyEmblemUrl &&
              order.detail.sanskritCharacterOrFamilyEmblem === `FAMILY_EMBLEM`
            "
            class="UserUploadImgTitle"
          >
            ユーザー選択画像(家紋)
          </div>
          <img
            v-if="
              order.detail.customFamilyEmblemUrl &&
              order.detail.sanskritCharacterOrFamilyEmblem === `FAMILY_EMBLEM`
            "
            title="ユーザー選択画像"
            class="UserUploadImg"
            :src="customFamilyEmblemImg"
            alt
          />
          <div
            v-if="
              order.detail.customSanskritCharacterUrl &&
              order.detail.sanskritCharacterOrFamilyEmblem === `SANSKRIT_CHARACTER`
            "
            class="UserUploadImgTitle"
          >
            ユーザー選択画像(梵字)
          </div>
          <img
            v-if="
              order.detail.customSanskritCharacterUrl &&
              order.detail.sanskritCharacterOrFamilyEmblem === `SANSKRIT_CHARACTER`
            "
            title="ユーザー選択画像"
            class="UserUploadImg"
            :src="customSanskritCharacterImg"
          />
          <div
            v-if="order.detail.customSanskritCharacterBackUrl"
            class="UserUploadImgTitle"
          >
            ユーザー選択画像(裏梵字)
          </div>
          <img
            v-if="order.detail.customSanskritCharacterBackUrl"
            title="ユーザー選択画像(裏梵字)"
            class="UserUploadImg"
            :src="customSanskritCharacterBackImg"
            alt
          />
          <InformationTable title="その他" :source="order.otherInformation" />

          <InformationTableImage
            title="注意文字"
            :source="order.itaijiPointsInformation"
          />
        </div>
      </div>
    </div>
    <OrderFormFooter
      message="入力内容をご確認ください"
      :canClickGoBack="true"
      @onClickGoBack="onClickGoBack"
      :canClickGoForward="false"
      @onClickGoForward="onClickGoForward"
    />
    <!-- Dialogs for order request and send -->
    <div id="OrderRequest">
      <el-dialog :visible.sync="requestDialogVisible" center>
        <span class="dialog-text">確認依頼を出しますか？</span>
        <span slot="footer" class="dialog-footer">
          <el-button
            class="cancel-btn"
            @click="requestDialogCancel"
          >
            キャンセル
          </el-button>
          <el-button
            class="blue-text"
            @click="requestOrder()"
          >
            依頼する
          </el-button>
        </span>
      </el-dialog>
      <OrderFormSendOrderDialog
        :visible.sync="sendOrderDialogVisible"
        :show-expected-shipping-picker="isExpectedShippingDateFormVisible"
        :submit-disabled="!isActiveSendButton"
        @closed="closedSendOrderDialogVisible"
        @cancel="sendOrderDialogCancel"
        @submit="sendOrder()"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

import OrderFormFooter from "@/components/OrderFormFooter";
import SignatureInput from "@/components/SignatureInput";
import InformationTable from "@/components/InformationTable";
import InformationTableChild from "@/components/InformationTableChild";
import InformationTableImage from "@/components/InformationTableImage";
import TabletPreview from "@/components/Common/TabletPreview";

import OrderUsecase from "@/usecases/order.usecase";
import FileUsecase from "@/usecases/file.usecase";
// import ProductUsecase from "@/usecases/product.usecase";
// import OrderFormUsecase from "@/usecases/orderForm.usecase";

import Auth from "@/services/auth.service";

import OrderFormSendOrderDialog from "@/components/OrderFormSendOrderDialog.vue";

export default {
  components: {
    InformationTable,
    InformationTableChild,
    InformationTableImage,
    OrderFormFooter,
    TabletPreview,
    SignatureInput,
    OrderFormSendOrderDialog,
  },
  inject: {
    getStep7OrderSubmitPayload: { default: null },
  },
  data () {
    return {
      isSignatureDialogVisible: false,
      dialogVisible: false,
      dialogFormState: 1,
      categories: [],
      customFamilyEmblemImg: "",
      customSanskritCharacterImg: "",
      customSanskritCharacterBackImg: "",
      requestDialogVisible: false,
      centerDialogVisible: false,
      sendOrderDialogVisible: false,
      setColumns: [],
      isLoading: false,
      points: [],
    };
  },
  async created () {
    this.getCustomImg();
  },
  mounted () {
    this.$store.commit("orderForm/setCurrentStep", 6);
    this.getPointsImages();
  },
  computed: {
    ...mapState({
      customerInput: state => state.orderForm.customerInput,
      categoryInput: state => state.orderForm.categoryInput,
      master: state => state.master,
      productInput: state => state.orderForm.productInput,
      orderTypeInput: state => state.orderForm.orderTypeInput,
      typographyInput: state => state.orderForm.typographyInput,
      detailInput: state => state.orderForm.detailInput,
      printType: state => state.orderForm.typographyInput.printType,
      form: state => state.orderForm.detailInput,
    }),
    ...mapGetters({
      previousStep: "orderForm/previousStep",
      order: "orderForm/order",
      customer: "orderForm/customer",
      detail: "orderForm/orderDetail",
      frontColor: "orderForm/frontColor",
      backColor: "orderForm/backColor",
      isEditMode: "orderForm/isEditMode",
    }),
    currentUser () {
      this.getCustomImg();
      return this.$store.state.user;
    },
    isClientContriburor () {
      return (
        this.currentUser && Auth.isClientContriburor(this.currentUser.role)
      );
    },
    computedShowRequestOrderButton () {
      const {
        isEditMode,
        order,
      } = this;
      const {
        status,
        parentOrderId,
      } = order;

      // 編集モードでなく（新規作成） && 親注文でない場合
      if (
        !isEditMode &&
        !parentOrderId
      ) {
        return true;
      }

      // 編集モード && 下書き状態 && 親注文でない場合
      if (
        isEditMode &&
        status === "DRAFT" &&
        !parentOrderId
      ) {
        return true;
      }

      return false;
    },
    // https://github.com/TheDesignium/nanokos/issues/321
    // 注文送信後に希望納期を変更されないようにしたいので、注文ステータスが「下書き・確認依頼中」以外の場合は希望納期の入力フォームを非表示にする
    isExpectedShippingDateFormVisible () {
      const {
        order,
        isEditMode,
      } = this;
      const {
        status,
      } = order;

      // 新規作成時、下書き、確認依頼中
      if (
        !isEditMode ||
        status === "DRAFT" ||
        status === "ORDER_REQUEST"
      ) {
        return true;
      }

      return false;
    },
    isActiveSendButton () {
      const {
        isEditMode,
      } = this;
      // 新規作成時はボタン表示
      if (!isEditMode) {
        return true;
      }
      if (!this.isExpectedShippingDateFormVisible) {
        return true;
      }

      return this.hasExpectedShippingDate;
    },
    // 希望納期が入力されているかの確認
    hasExpectedShippingDate () {
      return this.form && !!this.form.expectedShippingDate;
    },
  },
  watch: {},
  destroyed () {},
  methods: {
    ...mapActions({
      resetDetailInput: "orderForm/resetDetailInput",
    }),
    async getCustomImg () {
      if (this.order.detail.customFamilyEmblemUrl) {
        this.customFamilyEmblemImg = await usecase.file.fetchURL(
          this.order.detail.customFamilyEmblemUrl
        );
      }
      if (this.order.detail.customSanskritCharacterUrl) {
        this.customSanskritCharacterImg = await usecase.file.fetchURL(
          this.order.detail.customSanskritCharacterUrl
        );
      }
      if (this.order.detail.customSanskritCharacterBackUrl) {
        this.customSanskritCharacterBackImg = await usecase.file.fetchURL(
          this.order.detail.customSanskritCharacterBackUrl
        );
      }
    },
    getCustomSanskritCharacter () {},
    onSubmitSignature (imageUrl) {
      this.$store.commit("orderForm/updateDetail", { signature: imageUrl });
    },
    getPointsImages () {
      this.points = [];
      this.order.itaijiPoints.map(key => {
        return usecase.file.fetchURL(key).then(url => {
          this.points.push(url);
        });
      });
    },
    onClickGoForward () {
      this.dialogVisible = true;
    },
    onClickGoBack () {
      const { $router } = this;
      $router.push(this.previousStep);
    },
    _onClickSubmit () {},
    onClickConfirm () {
      this.dialogVisible = false;
      this.$notify({
        title: "注文をお受けいたしました",
        message: "3営業日以内に担当者からご連絡させていFただきます",
      });
      this.$router.push("/home");
    },
    /* to open request order dialog */
    getRequestOrderDialog () {
      this.requestDialogVisible = true;
      this.isLoading = true;
    },
    /* to cancel request order dialog */
    requestDialogCancel () {
      this.requestDialogVisible = false;
      this.isLoading = false;
    },
    /* to open send order dialog */
    getSendOrderDialog () {
      this.sendOrderDialogVisible = true;
      this.isLoading = true;
    },
    /* to cancel send order dialog */
    sendOrderDialogCancel () {
      this.sendOrderDialogVisible = false;
      this.isLoading = false;
    },
    /* request order when click 【確認依頼を出す】 button */
    requestOrder () {
      const { isEditMode } = this;
      const status = "ORDER_REQUEST";

      if (isEditMode) {
        this.updateOrderInformations(status);
      } else {
        this.saveOrderInformations(status);
      }
    },
    /* send order when click 【注文を送信する】button */
    sendOrder () {
      const { isEditMode } = this;
      const status = "PROCESSING";

      if (isEditMode) {
        this.updateOrderInformations(status);
      } else {
        this.saveOrderInformations(status);
      }
    },

    /**
     * [updateOrderInformations description]
     * @param   {[type]}  status  [status description]
     */
    async updateOrderInformations (status) {
      const orderId = this.$route.params.id;
      const loading = this.$loading();

      // set the status based on argument
      this.order.updateStatus(status);

      let payload = null;
      const getManagedPayload = this.getStep7OrderSubmitPayload;
      if (typeof getManagedPayload === "function") {
        payload = getManagedPayload(status);
      }
      if (!payload) {
        payload = this.order.orderCreatePayload;
      }
      payload.orderId = orderId;
      delete payload.order.parentOrderId;

      try {
        const res = await usecase.order.orderUpdate(payload);
        if (res.errors) {
          throw res;
        }
        const message = this.fetchCreateOrderMessage(status);

        // 一覧画面へ戻った際のステータスを変更しておく
        this.updateHomeState(status);

        // dismiss dialog
        this.requestDialogVisible = false;
        this.sendOrderDialogVisible = false;

        // transition to edit screen
        this.$router.push(`/orders/${orderId}`);

        this.$notify({
          message,
          duration: 3000,
        });
      } catch (err) {
        console.error(err);
        err.errors.map(el => {
          this.$notify.error(el.message);
        });
      } finally {
        this.isLoading = false;
        loading.close();
      }
    },

    /**
     * [updateHomeState description]
     * @param   {[type]}  status  [status description]
     */
    updateHomeState (status) {
      const item = sessionStorage.getItem("homeState");

      if (item) {
        const values = JSON.parse(item);
        values.status = status;

        sessionStorage.setItem("homeState", JSON.stringify(values));
      }
    },

    /* save order by request status or processing status */
    async saveOrderInformations (param) {
      const { $router } = this;
      const loading = this.$loading();

      // set the status based on argument
      this.order.updateStatus(param);

      let payload = null;
      const getManagedPayload = this.getStep7OrderSubmitPayload;
      if (typeof getManagedPayload === "function") {
        payload = getManagedPayload(param);
      }
      if (!payload) {
        payload = this.order.orderCreatePayload;
      }

      try {
        const createdOrder = await usecase.order.orderCreate(payload);
        const message = this.fetchCreateOrderMessage(param);

        // dismiss dialog
        this.requestDialogVisible = false;
        this.sendOrderDialogVisible = false;

        // transition to home screen
        $router.push(`/orders/${createdOrder.id}`);

        this.$notify({
          message,
          duration: 3000,
        });

        // this.$store.commit("orderForm/init");
      } catch (err) {
        console.error(err);
        err.errors.map(el => {
          this.$notify.error(el.message);
        });
      } finally {
        this.isLoading = false;
        loading.close();
      }
    },

    /**
     * [fetchCreateOrderMessage description]
     * @param   {[type]}  param  [param description]
     * @return  {[type]}         [return description]
     */
    fetchCreateOrderMessage (status) {
      const {
        quantity,
        productVariation,
        orderType,
      } = this.order.detail;
      const { stock } = productVariation;
      const { nameCount } = orderType;

      // 確認依頼の場合のメッセージ
      if (status === "ORDER_REQUEST") {
        return "確認依頼を出しました。";
      }

      // 欠品の場合のメッセージ
      // 「本数×霊数」で在庫を参照して在庫の有無を判断
      if (stock < quantity * nameCount) {
        return "ご注文ありがとうございました。誠に申し訳ございません。只今在庫欠品中の為、納期の確認を致します。";
      }

      return "ご注文ありがとうございました。お手続きを進めさせていただきます。";
    },

    /* to get order type informations */
    getOrderType () {
      const orderType = {
        type: this.orderTypeInput.type,
        nameCount: this.orderTypeInput.nameCount,
      };
      return orderType;
    },
    /* to get typography informations */
    getTypography () {
      const typography = {
        printType: this.printType,
        font: this.typographyInput.font,
        textColorFront: this.typographyInput.frontColor,
        textColorBack: this.typographyInput.backColor,
      };
      return typography;
    },
    /* to get familyEmblem */
    getFamilyEmblem () {
      const familyEmblem = {
        name: this.detailInput.familyEmblem.name,
        // imageUrl:
      };
      return familyEmblem;
    },
    /* to get getAgeAtDeathPrefix */
    getAgeAtDeathPrefix (detailInput) {
      return detailInput.ageAtDeathPrefix.isNeeded !== null
        ? detailInput.ageAtDeathPrefix.value
        : "";
    },
    /* to get buddhist name */
    getBuddhistName (i) {
      const buddhistName = this.detailInput.columns[i].buddhistName;
      return buddhistName !== undefined ? buddhistName : "";
    },
    /* to get under character */
    getUnderCharacter (i) {
      const underCharacters = this.detailInput.columns[i].underCharacters
        .isNeeded;
      return underCharacters !== null ? underCharacters : false;
    },
    /* to get secular name */
    getSecularName (i) {
      const secularName = this.detailInput.columns[i].secularName;
      return secularName !== undefined ? secularName : "";
    },
    /* to get title */
    getTitle (i) {
      const title = this.detailInput.columns[i].title.value;
      return title !== undefined ? title : "";
    },
    /* to get age at death */
    getAgeAtDeath (i) {
      if ("ageAtDeath" in this.detailInput.columns[i]) {
        return this.detailInput.columns[i].ageAtDeath;
      } else {
        return "";
      }
    },
    /* to get is needed layout reply */
    getLayoutReply (isNeeded) {
      // eslint-disable-next-line
      return isNeeded ? isNeeded : false;
    },
    /* to get expected shipping date */
    getShippingDate (shipDate) {
      console.log(shipDate);
      var date = new Date(shipDate);
      var year = date.getFullYear();
      var month = date.getMonth() + 1;
      var dt = date.getDate();
      if (dt < 10) {
        dt = "0" + dt;
      }
      if (month < 10) {
        month = "0" + month;
      }
      return shipDate ? year + "-" + month + "-" + dt : "";
    },

    /**
     * 種類のテキストを生成します。預かり位牌で種類の入力があれば azukariProductSize、それ以外であれば size を出力します。
     * @return {string} 種類のテキスト
     */
    getSizeText () {
      const {
        product,
        productVariation,
        azukariProductSize,
      } = this.order.detail;
      const { size } = productVariation;
      const isAzukariIhaiProduct = product.category.id === "99000";

      // 預かり位牌の場合
      if (isAzukariIhaiProduct) {
        return azukariProductSize || size;
      }

      return size;
    },

    /* 注文を送信しますか？のダイアログを閉じた場合、希望納期をリセットする  */
    closedSendOrderDialogVisible () {
      this.resetDetailInput();
      // 注文送信後にも希望納期がリセットされるか要確認
    },
  },

  /**
   * [beforeCreate description]
   */
  beforeCreate () {
    usecase.file = new FileUsecase();
    usecase.order = new OrderUsecase();
    // usecase.product = new ProductUsecase();
    // usecase.orderForm = new OrderFormUsecase();
  },

  /**
   * [beforeDestroy description]
   */
  beforeDestroy () {
    Object.keys(usecase).map(key => {
      delete usecase[key];
    });
  },
};

const usecase = {};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/variables.scss";

.OrderConfirmScreenContainer {
  height: 100%;
  margin-bottom: 8%;
  user-select: none;
}
.Container {
  display: grid;
  grid-template-columns: repeat(3, minmax(33.33%, 1fr));
  grid-template-rows: 100%;
}
.Container > * + * {
  border-left: 1px solid $default-border-color-base; // #cacaca;
  padding-left: 10px;
  padding-right: 10px;
}
@media (min-width: 767px) {
  .Container {
    height: 510px;
  }
}
@media (max-width: 1024px) {
  .Container {
    height: 520px;
  }
}
.ColumnTitle {
  font-weight: bold;
  font-size: 1.2rem;
  text-align: center;
}

.Preview-Body {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
}
.PreviewDescriptionArea {
  -webkit-box-flex: 1;
  -ms-flex: 1;
  flex: 1;
  text-align: center;
  font-weight: bold;
  padding-top: 0.7rem;
}
.product-variation {
  font-size: 18px;
}
.PreviewImageArea {
  flex: 1;
}
.PreviewImageArea-Image {
  display: block;
  margin: auto;
  width: 100%;
  max-width: 350px;
  padding-top: 0.7rem;
}
@media (max-width: 768px) {
  .PreviewImageArea-Image {
    margin-left: -3rem;
  }
}
.PreviewCenter {
  display: flex;
  flex-flow: column;
}
.DialogForm-Element {
  margin-bottom: 25px;
}
.InformationTableContainer {
  padding-right: 10px;
}
.smoothScroll {
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 10px;
  -webkit-overflow-scrolling: touch;
  height: 85%;
  white-space: pre-wrap;
}
.InformationTableContainer > * + * {
  margin-bottom: 35px;
}
</style>

<style lang="scss">
@import "~@/assets/styles/variables.scss";
#OrderRequest .dialog-text {
  font-size: 17px;
  font-weight: bold;
}
#OrderRequest .el-dialog--center .el-dialog__body {
  text-align: center;
  padding-bottom: 5rem;
  padding-top: 5rem;
  margin-top: 1rem;
}
#OrderRequest .el-button {
  border: none;
}
#OrderRequest .el-dialog__close {
  display: none;
}
#OrderRequest .blue-text {
  color: $blue-extra-deep;
}
#OrderRequest .blue-text.is-disabled {
  color: $--color-text-placeholder;
  border-color: $--border-color-lighter;
}
#OrderRequest .el-dialog__footer {
  // margin-top: 2.5rem !important;
  margin-left: 10%;
}
.SaveOrderButtonContainer {
  text-align: right;
  padding-bottom: 10px;
}

.DetailInformationContainer {
  height: 100%;
}
.center {
  text-align: center;
}
.UserUploadImg {
  height: 30%;
  margin-left: 100px;
}
.UserUploadImgTitle {
  font-size: 12px;
  font-weight: bold;
  margin-left: -30px;
}
</style>
